import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import HomePage from "./pages/HomePage";
import BookingPage from "./pages/BookingPage";
import SuppliesPage from "./pages/SuppliesPage";
import AdminPage from "./pages/AdminPage";
import LoginPage from "./pages/LoginPage";
import { Toaster } from "./components/ui/sonner";
import { Calendar, Settings, LogOut, Package, Home } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = ({ admin, onLogout }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isHome = location.pathname === "/";

  // Hide nav on home page
  if (isHome) return null;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5E5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#0A0A0A] font-bold text-base sm:text-lg"
            data-testid="nav-home"
          >
            <Home className="w-5 h-5 text-[#EA580C]" />
            <span className="hidden sm:inline">Casa Ciudadana Zapopan</span>
            <span className="sm:hidden">CCZ</span>
          </Link>
          <div className="flex items-center gap-1">
            {!isAdmin && (
              <>
                <Link
                  to="/reservaciones"
                  className={`flex items-center gap-1 px-3 py-2 text-xs sm:text-sm font-semibold uppercase tracking-wide transition-colors ${
                    location.pathname === "/reservaciones" ? "bg-[#EA580C] text-white" : "text-[#EA580C] hover:bg-[#EA580C]/10"
                  }`}
                  data-testid="nav-reservations"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Reservaciones</span>
                  <span className="sm:hidden">Reservar</span>
                </Link>
              </>
            )}
            {admin && isAdmin && (
              <button
                onClick={onLogout}
                className="flex items-center gap-1 px-3 py-2 text-xs sm:text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
                data-testid="nav-logout"
              >
                <LogOut className="w-4 h-4" />
                Salir
              </button>
            )}
            <Link
              to={isAdmin ? "/" : "/admin"}
              className="flex items-center gap-1 px-3 py-2 text-xs sm:text-sm font-semibold uppercase tracking-wide text-[#EA580C] hover:bg-[#EA580C] hover:text-white transition-colors"
              data-testid={isAdmin ? "nav-booking" : "nav-admin"}
            >
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">{isAdmin ? "Inicio" : "Admin"}</span>
              <span className="sm:hidden">{isAdmin ? "Inicio" : "Admin"}</span>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

function App() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { setAdmin(null); setChecking(false); return; }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      setAdmin(res.data);
    } catch { localStorage.removeItem("admin_token"); setAdmin(null); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const handleLogin = (user) => setAdmin(user);

  const handleLogout = async () => {
    try { const token = localStorage.getItem("admin_token"); await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }); } catch {}
    localStorage.removeItem("admin_token"); setAdmin(null);
  };

  return (
    <div className="app-container">
      <BrowserRouter>
        <Navigation admin={admin} onLogout={handleLogout} />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/reservaciones" element={<main className="pt-14"><BookingPage /></main>} />
          <Route path="/insumos" element={<main className="pt-14"><SuppliesPage /></main>} />
          <Route path="/admin/login" element={admin ? <Navigate to="/admin" replace /> : <main className="pt-14"><LoginPage onLogin={handleLogin} /></main>} />
          <Route path="/admin" element={checking ? null : (admin ? <main className="pt-14"><AdminPage token={localStorage.getItem("admin_token")} onLogout={handleLogout} /></main> : <Navigate to="/admin/login" replace />)} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
